import { computed, type MaybeRefOrGetter, toValue, watchEffect } from 'vue';
import {
  InfringementStatus,
  type Infringement,
  type InfringementSteps,
} from '@carvia/ros-client-types';

const requiredStepsByStatus: Partial<Record<InfringementStatus, (keyof InfringementSteps)[]>> = {
  [InfringementStatus.PENDING]: [],
  [InfringementStatus.EXTRACTING_DATA]: ['extractData', 'checkDuplicateFileNumber', 'findBooking'],
  [InfringementStatus.EXECUTING]: ['chargeFee', 'sendInvoice', 'sendAuthorityEmail'],
};

export const useInfringementStepState = (
  infringement: MaybeRefOrGetter<Infringement | null | undefined>,
) => {
  const stepStatusMap = computed(
    () =>
      (toValue(infringement)?.steps ?? {}) as Record<keyof InfringementSteps, string | undefined>,
  );

  const stepEntries = computed(() => Object.entries(stepStatusMap.value));
  const errorSteps = computed(() =>
    stepEntries.value.filter(([, status]) => status === 'error').map(([step]) => step),
  );

  const requiredSteps = computed(
    () => requiredStepsByStatus[toValue(infringement)?.status ?? InfringementStatus.PENDING] ?? [],
  );
  const hasErrorInCurrentStatus = computed(() =>
    requiredSteps.value.some((step) => stepStatusMap.value[step] === 'error'),
  );
  const areAllCurrentStatusStepsSuccessful = computed(
    () =>
      !requiredSteps.value.length ||
      requiredSteps.value.every((step) => stepStatusMap.value[step] === 'success'),
  );

  const showLoadingSpinner = computed(
    () => !hasErrorInCurrentStatus.value && !areAllCurrentStatusStepsSuccessful.value,
  );

  return {
    stepEntries,
    errorSteps,
    hasErrorInCurrentStatus,
    showLoadingSpinner,
  };
};
